import * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { LightProbeGrid } from 'three/addons/lighting/LightProbeGrid.js';
import { LightProbeGridHelper } from 'three/addons/helpers/LightProbeGridHelper.js';
import { createWorldSettings, createWorld, addBroadphaseLayer, addObjectLayer, enableCollision, registerAll, updateWorld, rigidBody, box, MotionType } from 'crashcat';
import { Vehicle, MAX_SPEED, BOOST_MULTIPLIER } from './Vehicle.js';
import { Camera } from './Camera.js';
import { Controls } from './Controls.js';
import { buildTrack, decodeCells, computeSpawnPosition, computeTrackBounds, TRACK_CELLS } from './Track.js';
import { buildWallColliders, createSphereBody } from './Physics.js';
import { SmokeTrails, BoostFlames } from './Particles.js';
import { DriftMarks } from './DriftMarks.js';
import { GameAudio } from './Audio.js';
import { LapTimer } from './LapTimer.js';
import { ColorMapGLTFLoader } from './Loader.js';
import SocketManager from './SocketManager.js';
import { MP_SERVER_URL } from './config.js';
import { showHomeScreen } from './HomeScreen.js';
import Leaderboard from './Leaderboard.js';
import Minimap from './Minimap.js';
import { NitroHUD } from './NitroHUD.js';
import { Speedometer } from './Speedometer.js';
import { ControlsHelp } from './ControlsHelp.js';

// Stagger pattern across the start line so multiplayer cars spawn side by
// side instead of stacked. Lateral steps alternate left/right around the
// center; rows wrap behind the line for >7 players.
function spawnOffsetFor( base, slotIndex ) {

	if ( ! slotIndex ) return base;
	const order = [ 0, 1, -1, 2, -2, 3, -3 ];
	const lateral = order[ slotIndex % order.length ] * 1.8;
	const back = - Math.floor( slotIndex / order.length ) * 2.5;
	const fx = Math.sin( base.angle );
	const fz = Math.cos( base.angle );
	const rx = Math.cos( base.angle );
	const rz = - Math.sin( base.angle );
	return {
		position: [
			base.position[ 0 ] + rx * lateral + fx * back,
			base.position[ 1 ],
			base.position[ 2 ] + rz * lateral + fz * back,
		],
		angle: base.angle,
	};

}

const _headingFwd = new THREE.Vector3();

function quatHeading( quat ) {

	_headingFwd.set( 0, 0, 1 ).applyQuaternion( quat );
	return Math.atan2( _headingFwd.x, _headingFwd.z );

}

// Show a quick toast and reload onto the room's locked track. The home
// screen reads ?room/?track/?mp from the URL and skips the menu, so the
// user lands straight on the right track.
function redirectToLockedTrack( session, lockedTrackId ) {

	const overlay = document.createElement( 'div' );
	overlay.style.cssText = 'position:fixed;inset:0;background:rgba(5,0,16,0.92);color:#d6f7ff;display:flex;align-items:center;justify-content:center;font:600 18px -apple-system,BlinkMacSystemFont,sans-serif;z-index:9999;letter-spacing:0.04em;text-shadow:0 0 8px rgba(0,245,255,0.6);';
	overlay.textContent = `joining room — switching to ${ lockedTrackId }…`;
	document.body.appendChild( overlay );

	const url = new URL( location.href );
	url.searchParams.set( 'mp', '1' );
	url.searchParams.set( 'mode', 'multi' );
	url.searchParams.set( 'room', session.roomId || 'default' );
	url.searchParams.set( 'track', lockedTrackId );
	if ( session.name ) url.searchParams.set( 'name', session.name );
	location.replace( url.toString() );

}


const renderer = new THREE.WebGLRenderer( { antialias: true, outputBufferType: THREE.HalfFloatType } );
renderer.setSize( window.innerWidth, window.innerHeight );
renderer.setPixelRatio( window.devicePixelRatio );
renderer.shadowMap.enabled = true;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;

const bloomPass = new UnrealBloomPass( new THREE.Vector2( window.innerWidth, window.innerHeight ) );
bloomPass.strength = 0.02;
bloomPass.radius = 0.02;
bloomPass.threshold = 0.5;

renderer.setEffects( [ bloomPass ] );

document.body.appendChild( renderer.domElement );

const scene = new THREE.Scene();
scene.background = new THREE.Color( 0xadb2ba );
scene.fog = new THREE.Fog( 0xadb2ba, 30, 55 );

const dirLight = new THREE.DirectionalLight( 0xffffff, 3 );
dirLight.position.set( 11.4, 15, -5.3 );
dirLight.castShadow = true;
dirLight.shadow.mapSize.setScalar( 4096 );
dirLight.shadow.camera.near = 0.5;
dirLight.shadow.camera.far = 60;
dirLight.shadow.radius = 4;
scene.add( dirLight );

const hemiLight = new THREE.HemisphereLight( 0xc8d8e8, 0x7a8a5a, 2 );
hemiLight.position.copy( dirLight.position )
scene.add( hemiLight );


window.addEventListener( 'resize', () => {

	renderer.setSize( window.innerWidth, window.innerHeight );

} );

const loader = new ColorMapGLTFLoader();

const modelNames = [
	'vehicle-truck-yellow', 'vehicle-truck-green', 'vehicle-truck-purple', 'vehicle-truck-red',
	'track-straight', 'track-corner', 'track-bump', 'track-finish',
	'decoration-empty', 'decoration-forest', 'decoration-tents',
];

const models = {};

async function loadModels() {

	const promises = modelNames.map( ( name ) =>
		new Promise( ( resolve, reject ) => {

			loader.load( `models/${ name }.glb`, ( gltf ) => {

				const meshes = [];
				gltf.scene.traverse( ( child ) => {

					if ( child.isMesh ) {

						child.material.side = THREE.FrontSide;
						meshes.push( child );

					}

				} );

				// Godot imports vehicle models at root_scale=0.5
				if ( name.startsWith( 'vehicle-' ) ) {

					gltf.scene.scale.setScalar( 0.5 );

				}

				if ( meshes.length === 1 ) {

					const mesh = meshes[ 0 ];
					mesh.removeFromParent();
					models[ name ] = mesh;

				} else {

					models[ name ] = gltf.scene;

				}

				resolve();

			}, undefined, reject );

		} )
	);

	await Promise.all( promises );

}

async function init() {

	registerAll();
	await loadModels();

	const mapParam = new URLSearchParams( window.location.search ).get( 'map' );
	let customCells = null;
	let spawn = null;

	if ( mapParam ) {

		try {

			customCells = decodeCells( mapParam );
			spawn = computeSpawnPosition( customCells );

		} catch ( e ) {

			console.warn( 'Invalid map parameter, using default track' );

		}

	}

	const session = await showHomeScreen( models );
	const isMultiplayer = session.mode === 'multi' && !! MP_SERVER_URL;

	// A `?map=` URL still wins (custom-built track). Otherwise honor the
	// home-screen track selection.
	if ( ! customCells && session.cells ) {

		customCells = session.cells;
		spawn = computeSpawnPosition( customCells );

	}

	// Compute track bounds and size physics/shadows to fit
	const bounds = computeTrackBounds( customCells );
	const hw = bounds.halfWidth;
	const hd = bounds.halfDepth;
	const groundSize = Math.max( hw, hd ) * 2 + 20;

	const shadowExtent = Math.max( hw, hd ) + 10;
	dirLight.shadow.camera.left = - shadowExtent;
	dirLight.shadow.camera.right = shadowExtent;
	dirLight.shadow.camera.top = shadowExtent;
	dirLight.shadow.camera.bottom = - shadowExtent;
	dirLight.shadow.camera.updateProjectionMatrix();

	scene.fog.near = groundSize * 0.4;
	scene.fog.far = groundSize * 0.8;

	buildTrack( scene, models, customCells );

	// Probes

	const probeHeight = 6;
	const probes = new LightProbeGrid(
		hw * 2, probeHeight, hd * 2,
		Math.max( 4, Math.round( hw / 4 ) ),
		2,
		Math.max( 4, Math.round( hd / 4 ) ),
	);
	probes.position.set( bounds.centerX, probeHeight / 2, bounds.centerZ );
	probes.bake( renderer, scene, { cubemapSize: 32, near: 0.1, far: groundSize } );
	scene.add( probes );

	// scene.add( new LightProbeGridHelper( probes, 0.5 ) );

	//

	const worldSettings = createWorldSettings();
	worldSettings.gravity = [ 0, - 9.81, 0 ];

	const BPL_MOVING = addBroadphaseLayer( worldSettings );
	const BPL_STATIC = addBroadphaseLayer( worldSettings );
	const OL_MOVING = addObjectLayer( worldSettings, BPL_MOVING );
	const OL_STATIC = addObjectLayer( worldSettings, BPL_STATIC );

	enableCollision( worldSettings, OL_MOVING, OL_STATIC );
	enableCollision( worldSettings, OL_MOVING, OL_MOVING );

	const world = createWorld( worldSettings );
	world._OL_MOVING = OL_MOVING;
	world._OL_STATIC = OL_STATIC;

	buildWallColliders( world, null, customCells );

	const roadHalf = groundSize / 2;
	rigidBody.create( world, {
		shape: box.create( { halfExtents: [ roadHalf, 0.01, roadHalf ] } ),
		motionType: MotionType.STATIC,
		objectLayer: OL_STATIC,
		position: [ bounds.centerX, - 0.125, bounds.centerZ ],
		friction: 5.0,
		restitution: 0.0,
	} );

	// Resolve spawn before the physics body is created so multiplayer slots
	// don't piggy-back into other karts.
	const baseSpawn = spawn || computeSpawnPosition( customCells || TRACK_CELLS );

	const sphereBody = createSphereBody( world, baseSpawn.position );

	const vehicle = new Vehicle();
	vehicle.rigidBody = sphereBody;
	vehicle.physicsWorld = world;

	{

		const [ sx, sy, sz ] = baseSpawn.position;
		vehicle.spherePos.set( sx, sy, sz );
		vehicle.prevModelPos.set( sx, 0, sz );
		vehicle.container.rotation.y = baseSpawn.angle;

	}

	const localModelKey = 'vehicle-truck-yellow';
	const vehicleGroup = vehicle.init( models[ localModelKey ] );
	scene.add( vehicleGroup );

	dirLight.target = vehicleGroup;

	const cam = new Camera();
	scene.add( cam.debug );

	const controls = new Controls();

	const particles = new SmokeTrails( scene );
	const boostFlames = new BoostFlames( scene );
	const driftMarks = new DriftMarks( scene, mapParam );

	const audio = new GameAudio();
	audio.init( cam.camera );

	const lapTimer = new LapTimer( customCells, mapParam );

	const nitroHUD = new NitroHUD();
	const speedometer = new Speedometer( { maxSpeed: MAX_SPEED * BOOST_MULTIPLIER, maxDisplaySpeed: 220 } );
	const controlsHelp = new ControlsHelp();
	const leaderboard = new Leaderboard();
	const minimap = new Minimap( customCells || TRACK_CELLS );

	const localId = 'local';
	leaderboard.setSelf( { id: localId, name: session.name } );
	leaderboard.addPlayer( { id: localId, name: session.name } );
	minimap.setSelf( localId );

	const socketManager = new SocketManager( scene, models );
	socketManager.setLocalVehicle( vehicle );
	socketManager.setLapTimer( lapTimer );

	socketManager.setCallbacks( {
		onWelcome: ( msg ) => {

			leaderboard.setSelf( { id: msg.self.id, name: msg.self.name } );
			leaderboard.removePlayer( localId );
			leaderboard.addPlayer( { id: msg.self.id, name: msg.self.name } );
			leaderboard.setRoom( ( session.roomId || 'default' ) + ' · ' + session.trackId );
			minimap.removePlayer( localId );
			minimap.setSelf( msg.self.id );
			for ( const peer of msg.peers ) {

				leaderboard.addPlayer( { id: peer.id, name: peer.name } );
				if ( peer.bestLap ) leaderboard.setBestLap( peer.id, peer.bestLap );
				if ( peer.currentLap ) leaderboard.setCurrentLap( peer.id, peer.currentLap );

			}

		},
		onJoined: ( peer ) => {

			leaderboard.addPlayer( { id: peer.id, name: peer.name } );
			if ( peer.bestLap ) leaderboard.setBestLap( peer.id, peer.bestLap );

		},
		onLeft: ( id ) => {

			leaderboard.removePlayer( id );
			minimap.removePlayer( id );

		},
		onLap: ( msg ) => {

			leaderboard.setBestLap( msg.playerId, msg.lapMs );
			if ( msg.currentLap ) leaderboard.setCurrentLap( msg.playerId, msg.currentLap );

		},
	} );

	if ( isMultiplayer ) {

		try {

			await socketManager.connect( MP_SERVER_URL, { roomId: session.roomId, name: session.name, trackId: session.trackId } );

			// The room locks its track at creation. If we picked a different
			// one we get redirected onto the room's track via a quick reload.
			if (
				socketManager.track &&
				socketManager.track !== session.trackId &&
				! mapParam
			) {

				redirectToLockedTrack( session, socketManager.track );
				return;

			}

			const offset = spawnOffsetFor( baseSpawn, socketManager.self?.spawnIndex || 0 );
			const [ ox, oy, oz ] = offset.position;
			vehicle.spherePos.set( ox, oy, oz );
			vehicle.prevModelPos.set( ox, 0, oz );
			vehicle.container.rotation.y = offset.angle;
			if ( vehicle.rigidBody && vehicle.physicsWorld ) {

				rigidBody.setPosition( vehicle.physicsWorld, vehicle.rigidBody, [ ox, oy, oz ], false );
				rigidBody.setLinearVelocity( vehicle.physicsWorld, vehicle.rigidBody, [ 0, 0, 0 ] );
				rigidBody.setAngularVelocity( vehicle.physicsWorld, vehicle.rigidBody, [ 0, 0, 0 ] );

			}

		} catch ( e ) {

			console.warn( 'mp connect failed, falling back to single-player', e );

		}

	}

	const _forward = new THREE.Vector3();
	const _camLead = new THREE.Vector3();

	const contactListener = {
		onContactAdded( bodyA, bodyB ) {

			if ( bodyA !== sphereBody && bodyB !== sphereBody ) return;

			_forward.set( 0, 0, 1 ).applyQuaternion( vehicle.container.quaternion );
			_forward.y = 0;
			_forward.normalize();

			const impactVelocity = Math.abs( vehicle.modelVelocity.dot( _forward ) );
			audio.playImpact( impactVelocity );

		}
	};

	const timer = new THREE.Timer();

	function animate() {

		requestAnimationFrame( animate );

		timer.update();
		const dt = Math.min( timer.getDelta(), 1 / 30 );

		const input = controls.update();

		updateWorld( world, contactListener, dt );

		vehicle.update( dt, input );

		dirLight.position.set(
			vehicle.spherePos.x + 11.4,
			15,
			vehicle.spherePos.z - 5.3
		);

		const mv = vehicle.modelVelocity;
		_camLead.set( 0, 0, 1 ).applyQuaternion( vehicle.container.quaternion ).multiplyScalar( Math.sqrt( mv.x * mv.x + mv.z * mv.z ) );
		cam.update( dt, vehicle.spherePos, _camLead );
		particles.update( dt, vehicle );
		boostFlames.update( dt, vehicle );
		driftMarks.update( dt, vehicle );
		audio.update( dt, vehicle.linearSpeed / MAX_SPEED, input.z, vehicle.driftIntensity );

		const hasInput = input.touchActive || Math.abs( input.x ) > 0.05 || Math.abs( input.z ) > 0.05;
		lapTimer.update( dt, vehicle.spherePos, hasInput );
		socketManager.update( dt );

		nitroHUD.update( vehicle );
		speedometer.update( vehicle );

		// Dev console handle — keep flat so the user can poke from devtools.
		if ( ! window.__racing ) window.__racing = { vehicle, socketManager, leaderboard, minimap, lapTimer, session };

		const selfId = socketManager.selfId || localId;
		minimap.setPlayer(
			selfId,
			vehicle.container.position.x,
			vehicle.container.position.z,
			vehicle.container.rotation.y
		);
		for ( const [ id, remote ] of socketManager.remotes ) {

			if ( ! remote.group ) continue;
			minimap.setPlayer(
				id,
				remote.group.position.x,
				remote.group.position.z,
				quatHeading( remote.group.quaternion )
			);

		}
		minimap.update();

		// Reflect local lap progress into the leaderboard.
		if ( lapTimer.bestLap != null ) leaderboard.setBestLap( selfId, lapTimer.bestLap * 1000 );
		leaderboard.setCurrentLap( selfId, lapTimer.lap );
		leaderboard.update( dt );

		renderer.render( scene, cam.camera );

	}

	animate();

}

init();
